import { useCallback, useEffect, useMemo, useState } from "react";

import { basketRepository } from "@/repositories/basketRepository";

/**
 * The four things a basket line needs to know about a product.
 *
 * Structural rather than the full `Product`, because the till grid sends a
 * deliberately lean payload — no cost price, no barcodes, no audit columns —
 * and a basket typed to the full record would force the till to fetch fields a
 * cashier must not see just to satisfy the compiler.
 */
export interface BasketProduct {
  id: string;
  name: string;
  selling_price: string;
  tax_rate: string;
}

/** One extra chosen at the till: its price delta, and enough to name it. */
export interface ChosenModifier {
  id: string;
  name: string;
  price_delta: string;
}

export interface BasketLine {
  /**
   * What identifies this line in the basket.
   *
   * The product id is **not** enough once modifiers exist: a plain kota and a
   * kota with extra cheese are two lines at two prices, and keying by product
   * would merge them and charge one of the customers wrongly. The key is the
   * product plus the sorted modifier ids, so tapping the same combination twice
   * still merges — which is what a cashier expects.
   */
  key: string;
  product: BasketProduct;
  quantity: number;
  modifiers: ChosenModifier[];
  /** Selling price plus the signed deltas. Preview only; the server prices again. */
  unitPrice: number;
}

/**
 * The basket, and the identity of the sale it will become.
 *
 * `saleId` and `idempotencyKey` are minted **once**, when the basket is created,
 * and kept until the sale completes or is cleared. That is the whole point: a
 * retry after a timeout must carry the same pair, or the server sees a new sale
 * rather than a repeat of one it may already have committed.
 *
 * Generating either of them inside the submit handler — the obvious place — would
 * defeat the idempotency completely while looking entirely correct.
 */
export function useBasket(cashierId?: string) {
  // Restored synchronously, so the first render already shows the basket a
  // power cut interrupted — see `basketRepository`.
  const [restored] = useState(() =>
    cashierId ? basketRepository.load<BasketLine>(cashierId) : null,
  );
  const [lines, setLines] = useState<BasketLine[]>(() => restored?.lines ?? []);
  const [identity, setIdentity] = useState(() =>
    restored ? { saleId: restored.saleId, idempotencyKey: restored.idempotencyKey } : newIdentity(),
  );

  // A restored basket whose sale already completed — the power went between
  // taking the money and clearing the screen — must not be charged again.
  useEffect(() => {
    if (!restored) return;
    let live = true;
    void basketRepository.wasCompleted(restored.saleId).then((completed) => {
      if (live && completed) {
        setLines([]);
        setIdentity(newIdentity());
      }
    });
    return () => {
      live = false;
    };
  }, [restored]);

  useEffect(() => {
    if (cashierId) basketRepository.save(cashierId, { lines, ...identity });
  }, [cashierId, lines, identity]);

  const add = useCallback(
    (product: BasketProduct, quantity = 1, modifiers: ChosenModifier[] = []) => {
      const key = lineKey(product.id, modifiers);
      const unitPrice =
        Number(product.selling_price) +
        modifiers.reduce((sum, modifier) => sum + Number(modifier.price_delta), 0);

      setLines((current) => {
        const existing = current.find((line) => line.key === key);
        if (existing) {
          return current.map((line) =>
            line.key === key ? { ...line, quantity: line.quantity + quantity } : line,
          );
        }
        return [...current, { key, product, quantity, modifiers, unitPrice }];
      });
    },
    [],
  );

  const setQuantity = useCallback((key: string, quantity: number) => {
    setLines((current) =>
      quantity <= 0
        ? current.filter((line) => line.key !== key)
        : current.map((line) => (line.key === key ? { ...line, quantity } : line)),
    );
  }, []);

  const remove = useCallback((key: string) => {
    setLines((current) => current.filter((line) => line.key !== key));
  }, []);

  /**
   * Put a held basket back, as a new sale with a new identity. Only into an
   * empty basket — the screen does not offer it otherwise.
   */
  const load = useCallback((held: BasketLine[]) => {
    setLines(held);
    setIdentity(newIdentity());
  }, []);

  /** Empty the basket **and** mint a new identity — this is a different sale now. */
  const clear = useCallback(() => {
    setLines([]);
    setIdentity(newIdentity());
  }, []);

  /**
   * The customer-facing figures, computed locally so the panel responds to every
   * tap without a round trip.
   *
   * A **preview**. The server prices the sale again from its own catalogue and
   * its own tax rules, and its answer is the one that is charged — the client
   * never sends a price. If the two disagree, the server is right, and the
   * difference is a bug worth knowing about rather than papering over.
   */
  const totals = useMemo(() => {
    const subtotal = lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);
    return {
      subtotal,
      itemCount: lines.length,
      unitCount: lines.reduce((sum, line) => sum + line.quantity, 0),
    };
  }, [lines]);

  return { lines, add, setQuantity, remove, clear, load, totals, ...identity };
}

function newIdentity() {
  return { saleId: crypto.randomUUID(), idempotencyKey: crypto.randomUUID() };
}

/**
 * The product plus its chosen extras, sorted.
 *
 * Sorted so that choosing cheese-then-chilli and chilli-then-cheese produce the
 * same line rather than two identical-looking ones a cashier cannot tell apart.
 */
function lineKey(productId: string, modifiers: ChosenModifier[]): string {
  if (modifiers.length === 0) return productId;
  return `${productId}::${modifiers
    .map((modifier) => modifier.id)
    .sort()
    .join(",")}`;
}
